/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Draggable,
  DropTarget,
  ConfirmModal,
  ConditionRuleProps,
  ReactSelectSearch,
  OptionTypeBase,
} from "@advapacs/common";
import { OnChangeValue } from "react-select";
import Condition from "./Condition";
import { useFlag } from "@unleash/proxy-client-react";

const ConditionRule: FunctionComponent<ConditionRuleProps> = ({
  id,
  disabled,
  parentId,
  createCondition,
  createConditionRule,
  onRemoveConditionRule,
  onRemoveCondition,
  onBooleanSelect,
  errors,
  onValueChangeHandler,
  value,
  onDropCondition,
  onDropConditionRule,
  ruleTypes,
  isDark,
  organisationOptions,
  siteOptions,
  modalityOptions,
  patientClassOptions,
  showModalityOperator,
  reportPriorityOptions,
  genderOptions,
  bodyPartOptions,
  examCodeOptions,
  onHandleScriptModal,
}: ConditionRuleProps) => {
  const { t } = useTranslation(["rule"]);
  const enabledDicomRegex = useFlag("dicom-condition-regex");

  const [canDrag, setCanDrag] = useState(false);
  const [data, setData] = useState(value);
  const [showChildren, setShowChildren] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const toggleChildren = () => {
    setShowChildren(!showChildren);
  };
  // Event Handlers for Condition Group
  const onAddConditionBtnHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (createCondition) {
      createCondition(id);
    }
  };

  const onAddConditionRuleBtnHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (createConditionRule) {
      createConditionRule(id);
    }
  };

  const onRemoveConditionRuleBtnHandler = (
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (parentId && onRemoveConditionRule) {
      setDeleting(true);
    }
  };

  const confirmRemove = () => {
    setDeleting(false);
    if (parentId && onRemoveConditionRule) {
      onRemoveConditionRule(id, parentId);
    }
  };

  const cancelRemove = () => {
    setDeleting(false);
  };

  useEffect(() => {
    if (!value) return;

    let newData = value;
    if (!enabledDicomRegex) {
      newData = {
        ...value,
        conditions: value.conditions.filter(
          (condition) =>
            !(
              condition.type === "DICOM" &&
              condition.dicom?.operator === "REGEX"
            )
        ),
      };
    }

    setData(newData);
  }, [value, enabledDicomRegex]);

  const options = [
    {
      label: t("and"),
      value: "AND",
    },
    {
      label: t("or"),
      value: "OR",
    },
  ];

  return (
    <Draggable
      className="drag-element"
      disableDrag={!canDrag}
      dataItem={JSON.stringify({ value: data, parentId })}
    >
      {}
      {deleting && (
        <ConfirmModal
          show={deleting}
          body={t("confirm_nested_condition_removal")}
          onClose={cancelRemove}
          cancel={cancelRemove}
          confirm={() => confirmRemove()}
        />
      )}

      <div className={`card-body condition-rule ${parentId ? "" : " p-0"}`}>
        <div className="card group-draggable">
          <div className="card-header header-elements-inline">
            <h5 className="card-title">
              <div className="d-block" style={{ minWidth: 85 }}>
                <ReactSelectSearch
                  isClearable={false}
                  isDisabled={disabled}
                  handleSelectChange={(
                    event: OnChangeValue<OptionTypeBase, false>
                  ) => onBooleanSelect(id, event?.value)}
                  defaultValue={{
                    label: t("and"),
                    value: "AND",
                  }}
                  options={options}
                  value={options.find(
                    (option) => option?.value === value?.operator
                  )}
                  isSearchable={false}
                  name="operator"
                  isDark={isDark}
                />
              </div>
            </h5>
            <div className="pull-right d-flex">
              {!disabled && (
                <>
                  <button
                    onClick={onAddConditionBtnHandler}
                    type="button"
                    className="add-condition btn btn-sm btn-secondary mr-1"
                  >
                    {t("add_condition")}
                  </button>
                  <button
                    onClick={onAddConditionRuleBtnHandler}
                    type="button"
                    className="add-group btn btn-sm btn-secondary mr-1"
                  >
                    {t("add_nested_condition")}
                  </button>
                </>
              )}
              {parentId && !disabled && (
                <button
                  onClick={onRemoveConditionRuleBtnHandler}
                  type="button"
                  className="remove-group btn btn-sm btn-danger mr-1"
                >
                  {t("remove_nested_condition")}
                </button>
              )}
              {parentId && (
                <div
                  tabIndex={0}
                  role="button"
                  className={` btn-sm p-lr-none m-r-xxs ${
                    !showChildren ? "icon-rotate-180" : ""
                  }`}
                  onClick={toggleChildren}
                  onKeyDown={toggleChildren}
                  data-action="collapse"
                >
                  {}
                </div>
              )}
              {parentId && !disabled && (
                <div
                  role="button"
                  tabIndex={0}
                  onMouseOver={() => {
                    setCanDrag(true);
                  }}
                  onFocus={() => {
                    setCanDrag(true);
                  }}
                  onMouseLeave={() => {
                    setCanDrag(false);
                  }}
                  style={{ marginTop: 2 }}
                  className={`p-lr-none btn-sm`}
                >
                  <i className="icon-move" />
                </div>
              )}
            </div>
          </div>
          {showChildren && (
            <>
              <div className="table-inline pl-2 mb-0 p-2 font-weight-bold">
                {t("type")}
              </div>
              {data.conditions.length === 0 &&
                data.conditionRules.length === 0 && (
                  <>
                    <div className="table-inline p-2">{t("no_conditions")}</div>
                  </>
                )}

              <DropTarget
                className="drop-target"
                hoverClass="drop-target-hover"
                onItemDropped={(item: string) => {
                  onDropCondition(id, item, 0);
                }}
              />
              {data.conditions.map((condition, index) => (
                <>
                  <Condition
                    organisationOptions={organisationOptions}
                    siteOptions={siteOptions}
                    onHandleScriptModal={onHandleScriptModal}
                    modalityOptions={modalityOptions}
                    patientClassOptions={patientClassOptions}
                    showModalityOperator={showModalityOperator}
                    reportPriorityOptions={reportPriorityOptions}
                    genderOptions={genderOptions}
                    bodyPartOptions={bodyPartOptions}
                    examCodeOptions={examCodeOptions}
                    isDark={isDark}
                    disabled={disabled}
                    errors={errors}
                    id={condition.id}
                    key={condition.id}
                    parentId={id}
                    type={condition.type}
                    condition={condition}
                    onRemoveCondition={onRemoveCondition}
                    onValueChangeHandler={onValueChangeHandler}
                    ruleTypes={ruleTypes}
                  />
                  <DropTarget
                    className="drop-target"
                    hoverClass="drop-target-hover"
                    onItemDropped={(item: string) => {
                      onDropCondition(id, item, index + 1);
                    }}
                  />
                </>
              ))}
              {data.conditionRules.map((conditionRule, index) => (
                <>
                  <ConditionRule
                    organisationOptions={organisationOptions}
                    siteOptions={siteOptions}
                    modalityOptions={modalityOptions}
                    showModalityOperator={showModalityOperator}
                    reportPriorityOptions={reportPriorityOptions}
                    patientClassOptions={patientClassOptions}
                    genderOptions={genderOptions}
                    bodyPartOptions={bodyPartOptions}
                    examCodeOptions={examCodeOptions}
                    isDark={isDark}
                    disabled={disabled}
                    errors={errors}
                    id={conditionRule.id}
                    key={conditionRule.id}
                    parentId={id}
                    value={conditionRule}
                    createCondition={createCondition}
                    onRemoveCondition={onRemoveCondition}
                    createConditionRule={createConditionRule}
                    onRemoveConditionRule={onRemoveConditionRule}
                    onBooleanSelect={onBooleanSelect}
                    onValueChangeHandler={onValueChangeHandler}
                    onDropCondition={onDropCondition}
                    onDropConditionRule={onDropConditionRule}
                    ruleTypes={ruleTypes}
                  />
                  <DropTarget
                    className="drop-target"
                    hoverClass="drop-target-hover"
                    onItemDropped={(item: string) => {
                      onDropConditionRule(id, item, index + 1);
                    }}
                  />
                </>
              ))}
            </>
          )}
        </div>
      </div>
    </Draggable>
  );
};

export default ConditionRule;
